import { WordReference, weigh } from "@circleofa/acim-engine"
import type { RendererRect } from "./canvas"
import { glueRendererRects } from "./glue-words"
import type { LineHelper } from "./line-helpers"
import { acim } from "@utils/acim"

export type WordSelectionPoint = {
  next : WordBox | null
  prev : WordBox | null
}

export const toWordSelectionPoint = (w : WordBox) : WordSelectionPoint => ({
  next: w,
  prev: w,
})

/**
 * Responsible for creating ranges but also for determining rect boxes.
 * This should be transient as well. And make sure wordBoxes are actually
 * set before creating this class.
 *
 * Autocorrects reversed ranges in the construction.
 */
export class WordBoxRange {
    // #range = new Range()
    #words: WordBox[]
    #offsetRects: RendererRect[] | null = null
    // #highestNumber: number | null = null
    constructor(
        public readonly start: WordBox,
        public readonly end: WordBox,
    ) {
        // The reason why do don't have the constructor use wordBoxes directly
        // is because sometimes this might be called before wordBoxes are rendered.
        // wordBoxes are intended to be transient, but WordRange can persist
        // because it actually connects to the

        if (WordBoxRange.isReversed(start, end)) {
            const tmpStart = this.start
            this.start = end
            this.end = tmpStart
        }

        // Get all wordboxes
        this.#words = this.getWordBoxesBetween()
        if (this.#words.length === 0)
            throw new Error("Word length was zero when trying to create a WordBoxRange")
    }

    static isReversed(start: WordBox, end: WordBox) {
        return weigh(start.word) > weigh(end.word)
    }

    /**
     * Just a helper function.
     * Same as constructor but returns null if either wordbox is null.
     * Must be forward.
     * @param from
     * @param to
     * @returns null if either `from` or `to` is null OR if `from` -> `to` is not forward
     */
    static fromMaybeWords(from : WordBox | null, to : WordBox | null) {
      if (!from || !to) return null
      if (this.isReversed(from, to)) return null
      return new WordBoxRange(from, to)
    }

    /**
     * Test forward and backward from points
     * @param a
     * @param b
     * @returns
     */
    static fromPoints(a : WordSelectionPoint, b : WordSelectionPoint) {
      const forward = WordBoxRange.fromMaybeWords(a.next, b.prev)
      if (forward) return forward
      const backward = WordBoxRange.fromMaybeWords(b.next, a.prev)
      if (backward) return backward
      return null
    }

    /**
     * Returns all wordBoxes between two wordboxes
     * @returns
     * @throws if the ranges are reversed
     */
    getWordBoxesBetween(): WordBox[] {
        if (this.start == this.end) return [this.start]
        if (this.start.lineHelper == this.end.lineHelper) {
            // Same line
            return this.start.lineHelper.boxes.slice(
                this.start.indexOfLine,
                this.end.indexOfLine + 1,
            )
        } else {
            // Different line
            // See if there are any lineHelpers in between
            const inBetween = this.start.lineHelper.container.lineHelpers
                .slice(this.start.lineHelper.lineIndex + 1, this.end.lineHelper.lineIndex)
                .flatMap((line) => line.boxes)
            const starts = this.start.lineHelper.boxes.slice(this.start.indexOfLine)
            const ends = this.end.lineHelper.boxes.slice(0, this.end.indexOfLine + 1)
            const combined = starts.concat(inBetween, ends)
            return combined
        }
    }

    public get topMostTop() {
        return this.getOffsetRects().reduce(
            (lowest, rect) => (rect.y < lowest ? rect.y : lowest),
            Infinity,
        )
    }

    public getDisplayWords() {
        return this.#words.map((word) => {
            return word.word
        })
    }

    /**
     * Returns a range with the total line
     */
    static FromLineHelpers(start: LineHelper, end: LineHelper) {
        if (!start.boxes.length || !end.boxes.length)
            throw new Error("One of the lineHelpers had no boxes")
        const startBox = start.boxes[0]
        const endBox = end.boxes[end.boxes.length - 1]
        return new WordBoxRange(startBox, endBox)
    }

    static SentenceRangeFromWord(word: WordBox) {
        const lineHelper = word.lineHelper
        const sentence = lineHelper.line.parentSentence
        let lineHelpers: LineHelper[]
        if (sentence) {
            lineHelpers = lineHelper.container.lineHelpers.filter(
                (helper) => helper.line.parentSentence === sentence,
            )
        } else {
            lineHelpers = [lineHelper]
        }
        return WordBoxRange.FromLineHelpers(lineHelpers[0], lineHelpers[lineHelpers.length - 1])
    }

    static ParagraphRangeFromWord(word: WordBox) {
        const lineHelper = word.lineHelper
        const paragraph = lineHelper.line.parentSentence?.parent
        let lineHelpers: LineHelper[]
        if (paragraph) {
            lineHelpers = lineHelper.container.lineHelpers.filter(
                (helper) => helper.line.parentSentence?.parent === paragraph,
            )
        } else {
            lineHelpers = [lineHelper]
        }
        return WordBoxRange.FromLineHelpers(lineHelpers[0], lineHelpers[lineHelpers.length - 1])
    }

    static OneWord(word: WordBox) {
        return new WordBoxRange(word, word)
    }

    /**
     * What kind of range is this? Is it within
     */
    get rangeType(): "in sentence" | "whole sentence" | "spanning" {
        if (this.start.lineHelper !== this.end.lineHelper) return "spanning"
        if (
            this.start.indexOfLine == 0 &&
            this.end.indexOfLine === this.end.lineHelper.boxes.length - 1
        )
            return "whole sentence"
        return "in sentence"
    }

    getOffsetRects(): RendererRect[] {
        if (this.#offsetRects !== null) return this.#offsetRects
        // Get all rects of the range, but we also want to add the line number if its greater than
        // the current selection

        // Get the sentence number if possible
        const includeSentenceNumber = this.rangeType !== "in sentence"

        const rects = this.#words.flatMap((word) => {
            return word.getRectsWithoutPadding(includeSentenceNumber)
        })

        const gluedRects = glueRendererRects(rects, 30, 5)
        this.#offsetRects = gluedRects
        return gluedRects
    }

    getSelectionBars(): WordSelectionBars {
        const left = WordSelectionBar.fromRendererRect(this.getOffsetRects()[0], true)
        const right = WordSelectionBar.fromRendererRect(
            this.getOffsetRects()[this.getOffsetRects().length - 1],
            false,
        )
        return new WordSelectionBars(left, right)
    }
}

/**
 * Used for getting information for the selection bar that you
 */
export class WordSelectionBar {
    constructor(
        public readonly x: number,
        public readonly y: number,
        public readonly height: number,
    ) {}

    static fromRendererRect(rect: RendererRect, left: boolean) {
        return new WordSelectionBar(left ? rect.x : rect.x + rect.width, rect.y, rect.height)
    }
}

/**
 * Contains both the left and right of {@link WordSelectionBar}
 */
export class WordSelectionBars {
    constructor(
        public readonly left: WordSelectionBar,
        public readonly right: WordSelectionBar,
    ) {}
}

/**
 * WordBoxes are transient. They are recreated every time a re-render is called
 */
export class WordBox {
    public readonly rects: WordBoxRect[] = []
    constructor(
        public readonly lineHelper: LineHelper,
        public readonly indexOfLine: number,
        domRects: DOMRect[],
        offsetX: number,
        offsetY: number,
    ) {
        domRects.forEach(({ left, top, width, height }) => {
            this.rects.push(new WordBoxRect(this, left - offsetX, top - offsetY, width, height))
        })
    }

    /**
     * Gets all the rects without padding and includes the sentence
     * number in the rect
     */
    getRectsWithoutPadding(includeSentenceNumber: boolean) {
        const rects = this.rects.map((rects) => rects.withoutPadding)
        if (!includeSentenceNumber) return rects
        if (this.word.indexInLine !== 0) return rects
        if (this.word.parentLine.indexInSentence !== 0) return rects
        return this.lineHelper.sentenceNumberRect.concat(rects)
    }

    get barLeft(): WordSelectionBar {
        return new WordSelectionBar(this.rects[0].x, this.rects[0].y, this.rects[0].height)
    }

    get barRight(): WordSelectionBar {
        return new WordSelectionBar(
            this.rects[this.rects.length - 1].right,
            this.rects[this.rects.length - 1].y,
            this.rects[this.rects.length - 1].height,
        )
    }

    get word() {
        return this.lineHelper.renderingWords[this.indexOfLine]
    }

    /**
     * Contains the processed range information include the actual HTML node
     * If start & end offset are undefined, we're passing a "whole node"
     */
    get rangeData(): {
        node: ChildNode
        startOffset?: number
        endOffset?: number
    } {
        const { startOffset, endOffset, nodeIndex } = this.word
        return {
            node: this.lineHelper.childNodes[nodeIndex],
            startOffset,
            endOffset,
        }
    }

    toWordReference(): WordReference {
        return new WordReference({ displayWord: this.word, acim })
    }
}

export interface EnhancedRendererRect extends RendererRect {
    bottom: number
    right: number
}
/**
 * Padding is so that when you're dragging across a gap,
 * it doesn't detect nothing. The idea is to have enough
 * padding to fill the vertical gaps between lines.
 */
const PADDING_Y = 3
export class WordBoxRect implements EnhancedRendererRect {
    public readonly bottom: number
    public readonly right: number
    public readonly withoutPadding: EnhancedRendererRect
    constructor(
        public readonly wordBox: WordBox,
        public readonly x: number,
        public readonly y: number,
        public readonly width: number,
        public readonly height: number,
    ) {
        // The without padding variation is used for rendering exclusively
        // to get those nice lines
        this.withoutPadding = {
            x,
            width,
            y,
            height,
            right: x + width,
            bottom: y + height,
        }
        this.bottom = y + height + PADDING_Y
        this.height = this.height + 2 * PADDING_Y
        this.y = this.y - PADDING_Y
        this.right = x + width
    }

    get word() {
        return this.wordBox.word
    }

    /**
     * If the rect is fully above the
     * @param y point of reference
     * @returns
     */
    isAbove(y: number) {
        return this.bottom < y
    }

    /**
     * If the rect is vertically inside the rect
     * @param y point of reference
     * @returns
     */
    isVerticallyInside(y: number) {
        return this.y <= y && y <= this.bottom
    }

    isBelow(y: number) {
        return this.y > y
    }

    /**
     * If the point is either horizontally inside the rect
     * or to left of that
     * @param x point of reference
     * @returns
     */
    toTheLeftOf(x: number) {
        return this.x < x
    }
    /**
     * The distance from the left of the rect to x
     * @param x point of reference
     * @returns
     */
    distanceToTheLeft(x: number) {
        return this.x - x
    }
}
